<script lang="ts">
  import ProductGallery from '$components/product-gallery.svelte';
  import type { GetProductQuery } from '$gql/storefront/operations';
  import type { MediaImage } from '$gql/storefront/schemas';
  import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';

  import { DEFAULT_TITLE, ProductMetafields } from '$lib/constants';
  import { cn } from '$lib/utils';
  import type { ImageDescriptionStoryblok } from '$types/bloks';

  import type { ShopifyImage } from '$types/shopify';

  export let product: NonNullable<GetProductQuery['product']>;
  export let selectedOptions: Record<string, string>;
  export let imageDescription: ImageDescriptionStoryblok[] | undefined;
  export let showVideoButton: boolean | undefined = undefined;

  let className: string | undefined = undefined;
  export { className as class };

  const shouldParseVariantImages = !!product.metafields.filter(
    (meta) => meta?.key === ProductMetafields.VARIANT_IMAGES
  )[0]?.value;

  const options = product.options;
  const images = product.images.nodes;

  const DEFAULT_IMAGES_KEY = 'default' as const;

  let imagesByVariant: Record<string, ShopifyImage[]> = {};
  let parsedImages = images;

  $: if (shouldParseVariantImages) {
    parsedImages = [
      ...new Set([
        ...options.map((opt) => imagesByVariant[selectedOptions[opt.name]] || []).flat(),
        ...imagesByVariant[DEFAULT_IMAGES_KEY]
      ])
    ];
  }

  if (shouldParseVariantImages) {
    const allOptions = [
      DEFAULT_IMAGES_KEY,
      ...options.map((opt) => opt.values.map((val) => val)).flat()
    ];

    let tmp: Record<string, ShopifyImage[]> = Object.fromEntries(
      allOptions.map((opt) => [opt, []])
    );

    images?.forEach((img) => {
      let wasIndexed = false;

      allOptions.forEach((opt) => {
        if (
          img.altText
            ?.replace(/\s/g, '')
            .toLowerCase()
            .includes(opt.replace(/\s/g, '').toLowerCase())
        ) {
          wasIndexed = true;
          tmp[opt] = [...tmp[opt], img];
        }
      });

      if (!wasIndexed) {
        tmp[DEFAULT_IMAGES_KEY] = [...tmp[DEFAULT_IMAGES_KEY], img];
      }
    });

    imagesByVariant = tmp;
  }

  $: parsedMedia = product.media.nodes
    ?.map((p) => {
      if (p.__typename === 'MediaImage') {
        const imageObject = p as MediaImage;
        const result = parsedImages.find((item) => item.url === imageObject.previewImage?.url);
        return result ?? p;
      }
      return p;
    })
    .filter((p) => p.__typename === 'Image' || p.__typename === 'Video');

  $: ATestVersionMediaIndexes = JSON.parse(
    product.metafields.find((p) => p?.key === ProductMetafields.A_TEST_VERSION_MEDIA_INDEX)
      ?.value || '[]'
  );
</script>

{#key parsedMedia || parsedImages}
  <ProductGallery
    images={parsedImages}
    media={parsedMedia}
    {ATestVersionMediaIndexes}
    {imageDescription}
    isPdp
    size="lg"
    class={cn(
      'aspect-[unset] h-[66vh] w-full lg:sticky lg:top-24 lg:h-[calc(100vh-96px-32px)] lg:max-h-[974px] lg:self-start rounded-md',
      className
    )}
    onImageDescriptionInteract={(title) => {
      const selectedVariant = product.variants.nodes.find(
        (variant) => variant.title === Object.values(selectedOptions).join(' / ')
      );

      let productVariantName = selectedVariant?.title ?? '';

      if (!productVariantName) {
        const availableVariant = product.variants.nodes.find((item) => item.availableForSale);
        productVariantName =
          availableVariant?.title && availableVariant?.title !== DEFAULT_TITLE
            ? availableVariant.title
            : product.title;
      }

      return analyticsEvent({
        event: AnalyticsEvents.IMAGE_DESCRIPTION_MODULE,
        title,
        items: [
          {
            item_id: product.handle,
            item_name: product.title,
            item_category: product.productType,
            item_variant: productVariantName
          }
        ]
      });
    }}
    options={product.options}
    {showVideoButton}
  />
{/key}
